import { useCallback, useState } from 'react';

/**
 * Schmale Navigation (Icon-Leiste statt vollem Menü) am Desktop.
 *
 * Die Wahl gilt je Kontext: Im KIAN-Fenster zählt jeder Pixel für den Chat, im Rest der App
 * das ausgeschriebene Menü. Wer KIAN aufklappt, klappt damit nicht die übrigen Seiten zu —
 * und umgekehrt. Gemerkt wird pro Gerät; ohne Speicher (privates Fenster, gesperrt) gelten
 * einfach die Vorgaben.
 */
export type NavContext = 'kian' | 'default';

const STORAGE_KEY: Record<NavContext, string> = {
  kian: 'fb.nav.compact.kian',
  default: 'fb.nav.compact.default',
};

const DEFAULT_COMPACT: Record<NavContext, boolean> = { kian: true, default: false };

export function navContextForPath(pathname: string): NavContext {
  return pathname === '/kian' || pathname.startsWith('/kian/') ? 'kian' : 'default';
}

function readStored(context: NavContext): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY[context]);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    // Speicher nicht verfügbar — Vorgabe gilt
  }
  return DEFAULT_COMPACT[context];
}

function writeStored(context: NavContext, compact: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY[context], compact ? '1' : '0');
  } catch {
    // Speicher nicht verfügbar — gilt dann nur bis zum Neuladen
  }
}

export function useCompactNav(pathname: string) {
  const context = navContextForPath(pathname);
  const [prefs, setPrefs] = useState<Record<NavContext, boolean>>(() => ({
    kian: readStored('kian'),
    default: readStored('default'),
  }));
  const compact = prefs[context];

  const toggle = useCallback(() => {
    const next = !compact;
    writeStored(context, next);
    setPrefs((prev) => ({ ...prev, [context]: next }));
  }, [compact, context]);

  return { compact, toggle };
}
